<template>
  <div class="wrraper app_versions_page">
    <!-- Start Breadcrumb -->
    <Breadcrumb :items="items" />
    <!-- End Breadcrumb -->

    <!-- Start Main Loader -->
    <transition name="fadeInUp" mode="out-in" v-if="loading">
      <MainLoader />
    </transition>
    <!-- End Main Loader -->

    <form v-else @submit.prevent="saveVersions">
      <div class="card-header mb-4">
        <h4 class="card-title">
          <i class="fas fa-mobile-alt ml-2"></i>
          {{ $t("appVersions.title") }}
        </h4>
        <p class="card-hint">{{ $t("appVersions.hint") }}</p>
      </div>

      <div class="row">
        <div
          class="col-lg-4 col-md-6"
          v-for="group in groupedVersions"
          :key="group.app_type"
        >
          <div class="version_card">
            <div class="version_card_header">
              <i :class="group.icon"></i>
              <span>{{ $t(`appVersions.apps.${group.app_type}`) }}</span>
            </div>

            <div
              class="input_wrapper top_label"
              v-for="row in group.rows"
              :key="`${row.app_type}-${row.platform}`"
            >
              <input
                type="text"
                class="form-control"
                dir="ltr"
                placeholder="1.0.0"
                v-model.trim="row.min_version"
              />
              <label class="form-label">
                <i :class="platformIcon(row.platform)" class="ml-1"></i>
                {{ $t(`appVersions.platforms.${row.platform}`) }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="buttons_wrapper">
        <button class="button_style_1" :disabled="btnIsLoading">
          {{ $t("forms.submit") }}
          <span class="btn_loader" v-if="btnIsLoading"></span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: "AppVersions",

  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.app_versions.title"),
          disabled: true,
          href: "",
        },
      ],

      loading: false,
      btnIsLoading: false,

      appTypes: [
        { app_type: "client", icon: "fas fa-user" },
        { app_type: "driver", icon: "fas fa-motorcycle" },
        { app_type: "restaurant", icon: "fas fa-store" },
      ],
      platforms: ["android", "ios"],

      // ========== Six rows: client/driver/restaurant x android/ios
      versions: [],
    };
  },

  computed: {
    groupedVersions() {
      return this.appTypes.map((app) => ({
        ...app,
        rows: this.versions.filter((row) => row.app_type === app.app_type),
      }));
    },
  },

  methods: {
    platformIcon(platform) {
      return platform === "ios" ? "fab fa-apple" : "fab fa-android";
    },

    // Always render the six combinations, filled from the api response
    buildVersions(data) {
      const rows = Array.isArray(data) ? data : [];

      this.versions = this.appTypes.reduce((acc, app) => {
        this.platforms.forEach((platform) => {
          const found = rows.find(
            (row) => row.app_type === app.app_type && row.platform === platform
          );
          acc.push({
            id: found ? found.id : null,
            app_type: app.app_type,
            platform,
            // `min_version` can be null, that is an empty input not an error
            min_version: found && found.min_version ? found.min_version : "",
          });
        });
        return acc;
      }, []);
    },

    getVersions() {
      this.loading = true;
      this.$axios({
        method: "GET",
        url: "mobile_app_versions",
      })
        .then((res) => {
          this.buildVersions(res.data.data);
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          if (err.response?.status === 403) {
            this.$router.replace({ name: "PermissionRequired" });
            return;
          }
          this.buildVersions([]);
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || this.$t("globalErr"),
            position: "bottomRight",
          });
        });
    },

    saveVersions() {
      if (this.btnIsLoading) return;
      this.btnIsLoading = true;

      this.$axios({
        method: "PUT",
        url: "mobile_app_versions",
        data: {
          versions: this.versions.map((row) => ({
            app_type: row.app_type,
            platform: row.platform,
            min_version: row.min_version ? row.min_version : null,
          })),
        },
      })
        .then((res) => {
          // Refresh the table from the response
          this.buildVersions(res.data.data);
          this.$iziToast.success({
            timeout: 2000,
            message: res.data.message || this.$t("editSuccess"),
            position: "bottomRight",
          });
          this.btnIsLoading = false;
        })
        .catch((err) => {
          // Keep the form values and show the api message
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || this.$t("globalErr"),
            position: "bottomRight",
          });
          this.btnIsLoading = false;
        });
    },
  },

  created() {
    this.buildVersions([]);
    this.getVersions();
  },
};
</script>

<style lang="scss" scoped>
.app_versions_page {
  .card-header {
    background: transparent;
    border: none;
    padding: 0;

    .card-title {
      font-weight: bold;
      margin-bottom: 4px;
    }

    .card-hint {
      font-size: 13px;
      color: #8a8a8a;
      margin: 0;
    }
  }

  .version_card {
    background: #fff;
    border-radius: 16px;
    padding: 18px;
    margin-bottom: 20px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);

    .version_card_header {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: bold;
      margin-bottom: 18px;
      padding-bottom: 10px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);

      i {
        color: var(--mainColor-2, #783d73);
      }
    }
  }

  .buttons_wrapper {
    button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
